// client/src/components/CourseList.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Button, Modal } from 'react-bootstrap';
import MapComponent from './MapComponent';
import { FaMapMarkedAlt } from 'react-icons/fa';
import CourseCard from './CourseCard';
import CourseDetails from './CourseDetails';
import BookingConfirmationModal from './BookingConfirmationModal';
import ScheduledBookingModal from './ScheduledBooking'; // Import the new modal
import StandbyBookingModal from './StandbyBookingModal';
import { initiateBooking, checkCourseCredential } from '../services/api';

const CourseList = ({ searchResults, date, filters }) => {
  // State for card expansion
  const [expandingCourseId, setExpandingCourseId] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  // State for immediate booking flow
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  // State for scheduled booking flow
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingCourse, setSchedulingCourse] = useState(null);

  // State for standby booking flow
  const [showStandbyModal, setShowStandbyModal] = useState(false);
  const [standbyCourse, setStandbyCourse] = useState(null);

  // State for the "you need to add a login for this course" prompt
  const [showCredentialPrompt, setShowCredentialPrompt] = useState(false);
  const [credentialPromptCourse, setCredentialPromptCourse] = useState(null);

  // Other state and hooks
  const [showMap, setShowMap] = useState(true);
  const navigate = useNavigate();

  const chunkArray = (array, size) => {
    if (!array) return [];
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Handler for the IMMEDIATE booking confirmation
  const handleConfirmBooking = async () => {
    if (!selectedBooking) return;
    setIsBooking(true);
    const messages = [
      "Connecting to booking service...", "Entering golfer information...",
      "Confirming tee time status...", "Finalizing booking details..."
    ];
    let messageIndex = 0;
    setProgressMessage(messages[messageIndex]);
    const intervalId = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setProgressMessage(messages[messageIndex]);
    }, 4000);

    const bookingData = {
      courseId: selectedBooking.course.id,
      date: selectedBooking.date,
      time: selectedBooking.time,
      golfers: selectedBooking.golfers
    };
    const result = await initiateBooking(bookingData);

    clearInterval(intervalId);
    setProgressMessage('');
    setIsBooking(false);
    setShowBookingModal(false);

    if (result.success) {
      alert(`Booking Successful! Your receipt number is: ${result.receiptNumber}`);
      navigate('/bookings');
    } else {
      alert(`Booking Failed: ${result.error || 'An unknown error occurred.'}`);
    }
  };

  // This handler opens the IMMEDIATE booking modal — first checking that the user has a
  // usable saved login for this course, so a missing credential can be surfaced as a clear
  // "go add it" prompt instead of letting the booking attempt fail later.
  const handleTimeClick = async (course, time) => {
    try {
      const { hasCredential } = await checkCourseCredential(course.id);
      if (!hasCredential) {
        setCredentialPromptCourse(course);
        setShowCredentialPrompt(true);
        return;
      }
    } catch (err) {
      console.error('Error checking course credential:', err);
      // Fail open — if the check itself errors out, let the existing server-side guard
      // in the booking flow handle it rather than blocking the user here.
    }

    setSelectedBooking({
      course,
      time,
      date,
      golfers: filters.golfers
    });
    setShowBookingModal(true);
  };

  // This handler opens the SCHEDULING modal
  const handleScheduleClick = (course) => {
    setSchedulingCourse(course);
    setShowScheduleModal(true);
  };

  // This handler opens the STANDBY modal
  const handleStandbyClick = (course) => {
    setStandbyCourse(course);
    setShowStandbyModal(true);
  };

  return (
    <>
      <Row className="mb-3">
        <Col style={{ position: 'relative', minHeight: showMap ? '300px' : 'auto' }}>
          {/* ✅ New single, restyled button positioned over the map container */}
          <Button
            variant="light"
            className="shadow-sm d-flex align-items-center"
            style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1000 }}
            onClick={() => setShowMap(!showMap)}
          >
            <FaMapMarkedAlt className="me-2" />
            {showMap ? 'Hide Map' : 'Show Map'}
          </Button>

          {/* Map is conditionally rendered inside the positioned container */}
          {showMap && <MapComponent filteredCourses={searchResults} />}
        </Col>
      </Row>

      <Row>
        {chunkArray(searchResults, 3).map((row, rowIndex) => {
          const isRowExpanded = row.some(
            (course) => course.id === expandedCard?.course?.id
          );
          const isAnyCardExpanded = expandedCard !== null;
          return (
            <div key={rowIndex} className="w-100">
              <Row
                className={`g-3 my-2 ${isRowExpanded ? 'd-none' : ''} ${isAnyCardExpanded && !isRowExpanded ? 'dimmed-row' : ''
                  }`}
              >
                {row.map((course, colIndex) => {
                  const isExpanding = expandingCourseId === course.id;
                  const entryPosition =
                    colIndex === 0 ? 'left' : colIndex === 1 ? 'center' : 'right';
                  return (
                    <Col md={4} key={course.id}>
                      <CourseCard
                        course={course}
                        date={date}
                        filters={filters}
                        isExpanding={isExpanding}
                        entryPosition={entryPosition}
                        onTitleClick={(course, allTeeTimes) => {
                          setExpandingCourseId(course.id);
                          setTimeout(() => {
                            setExpandedCard({ type: 'details', course, allTeeTimes });
                            setExpandingCourseId(null);
                          }, 200);
                        }}
                        onTimeClick={handleTimeClick}
                        onScheduleClick={handleScheduleClick}
                        onStandbyClick={handleStandbyClick}
                      />
                    </Col>
                  );
                })}
              </Row>
              {isRowExpanded && (
                <Row className="g-0 my-2">
                  <Col md={12} className="p-0">
                    <CourseDetails
                      course={expandedCard.course}
                      allTeeTimes={expandedCard.allTeeTimes}
                      onClose={() => setExpandedCard(null)}
                      filters={filters}
                      onTimeClick={handleTimeClick}
                      onScheduleClick={handleScheduleClick}
                      onStandbyClick={handleStandbyClick}
                    />
                  </Col>
                </Row>
              )}
            </div>
          );
        })}
      </Row>

      <BookingConfirmationModal
        show={showBookingModal}
        onHide={() => setShowBookingModal(false)}
        onConfirm={handleConfirmBooking}
        bookingDetails={selectedBooking}
        isLoading={isBooking}
        progressMessage={progressMessage}
        filters={filters}
      />

      {schedulingCourse && (
        <ScheduledBookingModal
          show={showScheduleModal}
          onHide={() => setShowScheduleModal(false)}
          course={schedulingCourse}
          targetDate={date}
        />
      )}

      {standbyCourse && (
        <StandbyBookingModal
          show={showStandbyModal}
          onHide={() => setShowStandbyModal(false)}
          course={standbyCourse}
          targetDate={date}
        />
      )}

      <Modal show={showCredentialPrompt} onHide={() => setShowCredentialPrompt(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Login Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            You haven't saved a login for {credentialPromptCourse?.name || 'this course'} yet.
            Add your username and password on your profile page to book here.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCredentialPrompt(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => navigate('/profile')}>
            Go to Profile
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CourseList;